import crypto from "../include/crypto";
import Router from "./Router";
import { HandlerInfo, SecurequServerConfig, ServerClientSecret, ServerClientOrigin, UploadFileMeta, Metadata } from "./types";

class SecurequServerBase extends Router {
   protected config: SecurequServerConfig;
   protected secret: string | null = null
   protected clients = new Map<ServerClientOrigin, ServerClientSecret>();
   protected uploadMeta = new Map<string, UploadFileMeta & { expire: number }>();
   readonly CONTENT_TYPE = "application/octet-stream";

   get RESPONSE_CONTENT_TYPE() {
      const isDev = this.config.mode === 'development'
      return isDev ? "application/json" : "application/octet-stream"
   }

   constructor(config: SecurequServerConfig) {
      super()
      if (!config.basepath) throw new Error("Basepath is required");
      if (!config.clients || Object.keys(config.clients).length === 0) throw new Error("Atleast one client is required");
      if (!config.basepath.startsWith("/")) config.basepath = `/${config.basepath}`;
      if (config.basepath.endsWith("/")) config.basepath = config.basepath.substring(0, config.basepath.length - 1);
      if (config.file) {
         config.file.maxFilesize = config.file.maxFilesize || 50 * 1024 // 50MB default
         config.file.checkFileType = config.file.checkFileType || true
      }
      config.mode = ["production", 'development'].includes(config.mode || "") ? config.mode : "production"
      this.config = config
      for (let client of this.config.clients) {
         this.clients.set(client.origin, client.secret)
      }
   }

   protected async getSecret() {
      if (!this.secret) {
         this.secret = await crypto.hash(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15))
      }
      return this.secret
   }

   protected async clientInfo(path: string, origin: string) {
      const isDev = path !== '/' && this.config.mode === 'development'
      let splitUrl = path.split("?")
      path = splitUrl[0]
      path = path.replace(this.config.basepath + "/", "")
      path = path.endsWith('/') ? path.substring(0, path.length - 1) : path
      path = path.startsWith('/') ? path.substring(1) : path

      const split = path.split("/")
      const hash = split.shift()
      let client = this.clients.get(origin)
      if (!client) {
         client = this.clients.get("*")
      }

      if (!hash?.length || !client) {
         throw new Response("Invalid client", { status: 403 });
      }

      let secret = client.substring(0, hash.length);
      let searchParams: { [key: string]: any } = {}
      if (splitUrl.length > 1) {
         const uparams = new URLSearchParams(decodeURIComponent(splitUrl[1]))
         if (isDev) {
            searchParams = Object.fromEntries(uparams)
         } else {
            const keys = Array.from(uparams.keys());
            if (keys.length) {
               const key = keys[0]
               const value = uparams.get(key) as string;
               const decrypted: any = await crypto.decrypt(value, secret)
               if (decrypted && decrypted.key === key) {
                  searchParams = decrypted.params || {}
               }
            }
         }
      }
      return {
         path: "/" + split.join('/'),
         secret,
         hash,
         searchParams
      }
   }

   protected async isValidSigneture(signeture: string | undefined, hash: string) {
      const serverSecret = await this.getSecret()
      if (this.secret && signeture) {
         let info: any = await crypto.decrypt(signeture, serverSecret)
         if (!info) throw new Response("Invalid Signeture", { status: 403 });
         let isHashValid = info.hash === hash
         let isNotExpired = info.expire > Date.now()
         if (!isHashValid) throw new Response("Invalid Signeture", { status: 403 });
         if (!isNotExpired) throw new Response("Signeture expired", { status: 403 });
      } else {
         throw new Response("Invalid Signeture", { status: 403 });
      }
   }

   async handleRequest(info: HandlerInfo, metadata?: Metadata) {
      if (this.config.accept) {
         const is = await this.config.accept(info, metadata)
         if (!is) throw new Response("Request not accepted", { status: 403 })
      }

      const { path, method } = info;
      let values: any = Object.values(this.routes[method]);
      for (let { test, handler } of values) {
         const match = test(path)
         if (match) {
            await handler({ ...info, params: match.params }, metadata)
         }
      }

      throw new Response("Request not accepted", { status: 404 })
   }
}

export default SecurequServerBase;